<!-- src/components/Topbar.vue -->
<template>
  <div class="navbar-custom">
    <div class="topbar container-fluid px-0">
      <div class="d-flex align-items-center gap-lg-2 gap-1">
        <div class="logo-topbar">
          <a href="index.html" class="logo-light">
            <span class="logo-lg">
              <img :src="`${$logoDir}logo.png`" alt="logo" />
            </span>
            <span class="logo-sm">
              <img :src="`${$logoDir}logo-sm.png`" alt="small logo" />
            </span>
          </a>
        </div>
        <button class="button-toggle-menu">
          <i class="mdi mdi-menu"></i>
        </button>
        <div class="d-none d-lg-block">
          <h6 class="font-20 fw-light">{{ $t('common.installerSpace') }}</h6>
        </div>
      </div>

      <ul class="topbar-menu d-flex align-items-center gap-3">
        <li class="dropdown">
          <a class="nav-link dropdown-toggle arrow-none" data-bs-toggle="dropdown" href="#" role="button"
            aria-haspopup="false" aria-expanded="false">
            <span v-if="currentFlag" :class="`fi fi-${currentFlag} me-0 me-sm-1`"></span>
            <span class="align-middle d-none d-lg-inline-block">{{ currentLabel }}</span>
            <i class="mdi mdi-chevron-down d-none d-sm-inline-block align-middle"></i>
          </a>
          <div class="dropdown-menu dropdown-menu-end dropdown-menu-animated">
            <a v-for="lang in languages" :key="lang.code" href="javascript:void(0);" class="dropdown-item" @click="setLocale(lang.code)">
              <span v-if="lang.flag" :class="`fi fi-${lang.flag} me-1`"></span>
              <span class="align-middle">{{ lang.label }}</span>
            </a>
          </div>
        </li>
        <li class="dropdown">
          <a class="nav-link dropdown-toggle arrow-none" data-bs-toggle="dropdown" href="#" role="button" aria-haspopup="false" aria-expanded="false" :title="$t('support.menuTitle')">
            <span class="position-relative d-inline-block">
              <i class="uil uil-info-circle font-22"></i>
              <span v-if="supportUnread > 0" class="badge bg-danger rounded-pill support-unread-badge">{{ supportUnread }}</span>
              <span v-else-if="hasNewReleaseNotes" class="release-notes-dot"></span>
            </span>
          </a>
          <div class="dropdown-menu dropdown-menu-end dropdown-menu-animated">
            <a href="javascript:void(0);" class="dropdown-item" @click="openSupport">
              <i class="uil uil-headphones-alt me-1"></i>{{ $t('support.contactRequest') }}
              <span v-if="supportUnread > 0" class="badge bg-danger rounded-pill ms-1">{{ supportUnread }}</span>
            </a>
            <a href="javascript:void(0);" class="dropdown-item" @click="openReleaseNotes">
              <i class="uil uil-arrow-growth me-1"></i>{{ $t('releaseNotes.title') }}
              <span v-if="hasNewReleaseNotes" class="badge bg-danger rounded-pill ms-1">&nbsp;</span>
            </a>
          </div>
        </li>
        <li class="d-none d-sm-inline-block">
          <div class="nav-link" id="light-dark-mode" data-bs-toggle="tooltip" data-bs-placement="left"
            title="Theme Mode">
            <i class="ri-moon-line font-22"></i>
          </div>
        </li>
        <li class="d-none d-md-inline-block">
          <a class="nav-link" href="" data-toggle="fullscreen">
            <i class="ri-fullscreen-line font-22"></i>
          </a>
        </li>
        <li class="dropdown">
          <a class="nav-link dropdown-toggle arrow-none nav-user px-2" data-bs-toggle="dropdown" href="#" role="button"
            aria-haspopup="false" aria-expanded="false">
            <span class="account-user-avatar">
              <img v-if="installer.picture" :src="installer.picture" alt="user-image" width="48" height="48" class="rounded-circle" />
              <div v-else class="avatar-sm rounded-circle">
                <span class="avatar-title rounded-circle bg-info-lighten text-info">{{ installer.initials }}</span>
              </div>
            </span>
            <span class="d-lg-flex flex-column gap-1 d-none">
              <h5 class="my-0">{{ installer.full_name }}</h5>
              <h6 class="my-0 fw-normal">{{ installer.company_name }}</h6>
            </span>
          </a>
          <div class="dropdown-menu dropdown-menu-end dropdown-menu-animated profile-dropdown">
            <router-link to="/account" class="dropdown-item">
              <i class="mdi mdi-account-circle me-1"></i>
              <span>{{ $t('nav.account') }}</span>
            </router-link>
            <router-link to="/logout" class="dropdown-item">
              <i class="mdi mdi-logout me-1"></i>
              <span>{{ $t('nav.logout') }}</span>
            </router-link>
          </div>
        </li>
      </ul>
    </div>
  </div>
  <ReleaseNotesPanel ref="releaseNotesPanel" @seen="hasNewReleaseNotes = false" />
  <SupportPanel ref="supportPanel" />
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLocale as applyLocale } from '@/i18n';
import ReleaseNotesPanel from '@/components/ReleaseNotesPanel.vue';
import SupportPanel from '@/components/SupportPanel.vue';

export default {
  name: "TopBar",
  setup() {
    const userStore = useUserStore();
    const { locale } = useI18n();

    const installer = ref(userStore.installer);

    watch(() => userStore.installer, (updatedInstaller) => {
      installer.value = updatedInstaller;
    });

    const languages = [
      { code: 'fr', label: 'Français', flag: 'fr' },
      { code: 'en', label: 'English', flag: 'gb' },
      { code: 'it', label: 'Italiano', flag: 'it' },
      { code: 'es', label: 'Español', flag: 'es' },
      { code: 'de', label: 'Deutsch', flag: 'de' },
      { code: 'nl', label: 'Nederlands', flag: 'nl' },
      { code: 'pt', label: 'Português', flag: 'pt' },
      { code: 'co', label: 'Corsu', flag: 'corsu' },
    ];

    const setLocale = (lang) => {
      applyLocale(lang);
    };

    const currentLang = computed(() =>
      languages.find(l => l.code === locale.value) ?? languages[0]
    );

    const currentFlag = computed(() => currentLang.value.flag);
    const currentLabel = computed(() => currentLang.value.label);

    const releaseNotesPanel = ref(null);
    const hasNewReleaseNotes = ref(false);
    const supportPanel = ref(null);
    const supportUnread = computed(() => userStore.supportUnread);

    watch(() => userStore.supportPanelRequested, (requested) => {
      if (requested) {
        userStore.supportPanelRequested = false;
        supportPanel.value?.show();
      }
    });

    onMounted(async () => {
      await userStore.loadSupportRequests();

      const notes = await userStore.loadReleaseNotes();
      if (notes.length) {
        const latest = Math.max(...notes.map(n => n.id));
        const lastSeen = parseInt(localStorage.getItem('lastSeenReleaseNoteId')) || 0;
        hasNewReleaseNotes.value = latest > lastSeen;
      }
    });

    const openReleaseNotes = () => releaseNotesPanel.value.show();
    const openSupport = () => supportPanel.value.show();

    return {
      installer,
      languages,
      setLocale,
      currentFlag,
      currentLabel,
      releaseNotesPanel,
      hasNewReleaseNotes,
      openReleaseNotes,
      supportPanel,
      supportUnread,
      openSupport,
    };
  },
  components: {
    ReleaseNotesPanel,
    SupportPanel,
  },
};
</script>

<style scoped>
.support-unread-badge {
  position: absolute;
  top: -4px;
  right: -10px;
  font-size: 0.6rem;
}

.release-notes-dot {
  position: absolute;
  top: -2px;
  right: -5px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #dc3545;
  border: 2px solid #fff;
}
</style>
